/**
 * The Leitweg-ID — the address a German public body receives e-invoices under.
 *
 * MIRROR of `new-backend/admin/src/lib/organizations/leitwegId.ts`, which is
 * the authority and refuses the same values on save. The form checks too, so
 * a typo is named at the field, in the operator's language, before anything is
 * sent. Change both together; both test suites load the same samples from
 * `shared/organizations/leitweg-id-vectors.json`.
 *
 * Shape: coarse address (2–12 digits) · optional fine address (up to 30
 * letters or digits) · two check digits, joined by hyphens. The check digits
 * are ISO/IEC 7064 MOD 97-10 over the ID without hyphens, letters counted
 * A=10 … Z=35 (as in an IBAN): a valid ID gives 1.
 */

/** The longest valid Leitweg-ID (12 + 1 + 30 + 1 + 2). */
export const LEITWEG_ID_MAX_LENGTH = 46;

const SHAPE = /^\d{2,12}(-[A-Z0-9]{1,30})?-\d{2}$/;

const checksumValid = (id: string): boolean => {
    let remainder = 0;
    for (const char of id.replace(/-/g, '')) {
        for (const digit of String(Number.parseInt(char, 36))) {
            remainder = (remainder * 10 + Number(digit)) % 97;
        }
    }
    return remainder === 1;
};

export type LeitwegIdProblem = 'format' | 'check_digits';

/**
 * What is wrong with a typed Leitweg-ID, or null when it is valid or empty
 * (the field is optional). Case and surrounding spaces do not matter; the
 * backend stores the ID upper-case.
 */
export const leitwegIdProblem = (value: string): LeitwegIdProblem | null => {
    const id = value.trim().toUpperCase();
    if (id === '') return null;
    if (id.length > LEITWEG_ID_MAX_LENGTH || !SHAPE.test(id)) return 'format';
    return checksumValid(id) ? null : 'check_digits';
};
